import { useState, FormEvent } from 'react';

interface WaitlistFormProps {
  apiUrl: string;
  buttonText?: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export function WaitlistForm({
  apiUrl,
  buttonText = 'Join the Waitlist',
}: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [ehr, setEhr] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(`${apiUrl}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || undefined,
          ehr: ehr || undefined,
          source: 'website',
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || "Thanks for joining. We'll be in touch soon.");
        setEmail('');
        setName('');
        setEhr('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Unable to connect. Please check your internet connection.');
    }
  };

  const isSubmitting = status === 'submitting';

  return (
    <form onSubmit={handleSubmit} className="waitlist-form">
      <div className="form-fields">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="form-input"
          disabled={isSubmitting}
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Work email"
          required
          className="form-input"
          disabled={isSubmitting}
        />
        <select
          value={ehr}
          onChange={(e) => setEhr(e.target.value)}
          className="form-input form-select"
          disabled={isSubmitting}
        >
          <option value="">EHR System (optional)</option>
          <option value="practice_fusion">Practice Fusion</option>
          <option value="athenahealth">Athenahealth</option>
          <option value="elation">Elation</option>
          <option value="drchrono">DrChrono</option>
          <option value="eclinicalworks">eClinicalWorks</option>
          <option value="nextgen">NextGen</option>
          <option value="other">Other</option>
        </select>
        <button type="submit" className="form-button" disabled={isSubmitting}>
          {isSubmitting ? 'Joining...' : buttonText}
        </button>
      </div>

      {status === 'success' && (
        <div className="form-message form-message-success">{message}</div>
      )}
      {status === 'error' && (
        <div className="form-message form-message-error">{message}</div>
      )}
    </form>
  );
}
